/**
 * Browser ad pixels: Meta, TikTok and GA4 (Google Ads imports its conversions
 * from GA4). Each loads only when its id is set at build time and the visitor
 * has not declined (see consent.ts):
 *   VITE_META_PIXEL_ID, VITE_TIKTOK_PIXEL_ID, VITE_GA4_ID
 *
 * The pixels only report page views. Sign-ups and purchases are sent by the
 * API (server-side), which is more reliable than the browser.
 */
import { adConsent, needsConsentPrompt, setAdConsent } from './consent.js';

const env = import.meta.env as Record<string, string | undefined>;
const META_ID = env['VITE_META_PIXEL_ID']?.trim();
const TIKTOK_ID = env['VITE_TIKTOK_PIXEL_ID']?.trim();
const GA4_ID = env['VITE_GA4_ID']?.trim();

type Fn = (...args: unknown[]) => void;
type Win = Window & { fbq?: Fn; ttq?: { page: () => void }; gtag?: Fn; dataLayer?: unknown[] };

let loaded = false;

function inline(code: string): void {
  const s = document.createElement('script');
  s.textContent = code;
  document.head.appendChild(s);
}

function load(): void {
  if (loaded) return;
  loaded = true;
  const id = (v: string): string => JSON.stringify(v);

  if (META_ID) {
    // Meta's standard base code.
    inline(`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init',${id(META_ID)});fbq('track','PageView');`);
  }
  if (TIKTOK_ID) {
    // TikTok's standard base code.
    inline(`!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=d.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=d.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};ttq.load(${id(TIKTOK_ID)});ttq.page();}(window,document,'ttq');`);
  }
  if (GA4_ID) {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_ID)}`;
    document.head.appendChild(s);
    inline(`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config',${id(GA4_ID)});`);
  }

  // Hash routes are page views too.
  window.addEventListener('hashchange', () => {
    const w = window as Win;
    w.fbq?.('track', 'PageView');
    w.ttq?.page();
    w.gtag?.('event', 'page_view', { page_location: window.location.href, page_title: document.title });
  });
}

function showConsentBar(): void {
  const bar = document.createElement('div');
  bar.className = 'consent-bar';
  bar.setAttribute('role', 'dialog');
  bar.setAttribute('aria-label', 'Cookie consent');
  bar.innerHTML = `
    <p>We use cookies from Meta, TikTok and Google to measure our ads.
      <a href="/legal/privacy.html" target="_blank" rel="noopener">Privacy</a></p>
    <div class="consent-actions">
      <button class="btn btn-secondary" type="button" data-consent="no">Decline</button>
      <button class="btn btn-primary" type="button" data-consent="yes">Accept</button>
    </div>`;
  bar.addEventListener('click', (e) => {
    const choice = (e.target as HTMLElement).closest<HTMLElement>('[data-consent]')?.dataset['consent'];
    if (!choice) return;
    setAdConsent(choice === 'yes');
    bar.remove();
    if (choice === 'yes') load();
  });
  document.body.appendChild(bar);
}

/** Boot: load the pixels, or ask first in Europe. No-op when no pixel id is set. */
export function initPixels(): void {
  if (!META_ID && !TIKTOK_ID && !GA4_ID) return;
  if (needsConsentPrompt()) {
    showConsentBar();
    return;
  }
  if (adConsent()) load();
}
